/**
 * ConvertToOpportunityDialog — preview + override field mapping before creating opp.
 * Idempotent: if already converted, shows existing opportunity_id.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Sparkles, ChevronRight, Loader2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { toast } from 'sonner';

export function ConvertToOpportunityDialog({ open, submission, onClose, onConverted }) {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [proposed, setProposed] = useState(null);

  useEffect(() => {
    if (!open || !submission) {
      setProposed(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/lead-submissions/${submission.id}/convert-preview`);
        if (!cancelled) setProposed(res?.proposed || {});
      } catch (e) {
        if (!cancelled) toast.error('Could not load mapping preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submission?.id]);

  const onConfirm = async () => {
    if (!submission) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/lead-submissions/${submission.id}/convert-to-opportunity`, proposed || {});
      const oppId = res?.opportunity_id;
      if (!oppId) throw new Error('no opportunity returned');
      toast.success('Opportunity created');
      onConverted(oppId, submission.id);
    } catch (e) {
      toast.error('Could not convert submission');
    } finally {
      setSubmitting(false);
    }
  };

  if (!submission) return null;

  const update = (k, v) => setProposed((prev) => ({ ...(prev || {}), [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[#FFFEF5]" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#B89B5E]" /> Convert to Opportunity
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-stone-600">
          We'll map this submission into a new opportunity in <strong>Discovery</strong> stage.
          Adjust the fields below before confirming — extras land in notes.
        </p>

        {loading || !proposed ? (
          <div className="py-12 text-center text-stone-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Company Name</Label>
              <Input value={proposed.company_name || ''} onChange={(e) => update('company_name', e.target.value)} className="bg-white" data-testid="convert-company" />
            </div>
            <div>
              <Label className="text-xs">Contact Name</Label>
              <Input value={proposed.contact_name || ''} onChange={(e) => update('contact_name', e.target.value)} className="bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contact Title</Label>
                <Input value={proposed.contact_title || ''} onChange={(e) => update('contact_title', e.target.value)} placeholder="e.g. Owner" className="bg-white" data-testid="convert-title" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={proposed.contact_phone || ''} onChange={(e) => update('contact_phone', e.target.value)} className="bg-white" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={proposed.contact_email || ''} onChange={(e) => update('contact_email', e.target.value)} className="bg-white" />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={onConfirm} disabled={loading || submitting} className="bg-[#B89B5E] hover:bg-[#A88A4D] text-white" data-testid="convert-confirm-btn">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Converting…</> : <>Create Opportunity <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
